import { instance } from "api/apiBase";
import { User } from "views/settings-users/types";

const NO_RELATED_TOOLTIP = "Select a related object first";

const getNoAccessTooltip = (propertyName?: string) =>
  `This user does not have access to ${propertyName ?? "this property"}. Contact your admin to update their permissions.`;

const getMentionTooltip = ({
  propertyName,
  disabled
}: {
  propertyName?: string;
  disabled?: boolean;
}) => {
  if (disabled && propertyName) return getNoAccessTooltip(propertyName);

  return undefined;
};

export const computePropertyAccessState = ({
  user,
  propertyId,
  propertyName,
  requirePropertyAccess,
  hasRelatedObject
}: {
  user: {
    can_manage_all_properties?: boolean;
    properties?: { id: number }[];
    property_ids?: number[];
  };
  propertyId?: number;
  propertyName?: string;
  requirePropertyAccess?: boolean;
  hasRelatedObject?: boolean;
}): { disabled: boolean; tooltip?: string } => {
  if (!requirePropertyAccess) return { disabled: false };
  if (user.can_manage_all_properties) return { disabled: false };
  if (!hasRelatedObject) return { disabled: true, tooltip: NO_RELATED_TOOLTIP };
  if (!propertyId) return { disabled: false };
  const hasAccess =
    user.property_ids?.includes(propertyId) ??
    user.properties?.some((p) => p.id === propertyId) ??
    false;
  if (hasAccess) return { disabled: false };
  return { disabled: true, tooltip: getNoAccessTooltip(propertyName) };
};

export const buildLocalMentionSections = ({
  query,
  users,
  propertyId,
  propertyName,
  requirePropertyAccess,
  hasRelatedObject
}: {
  query: string;
  users: User[];
  propertyId?: number;
  propertyName?: string;
  requirePropertyAccess?: boolean;
  hasRelatedObject?: boolean;
}) => {
  const term = query.trim().toLowerCase();
  const filtered = term
    ? users.filter(
        (u) =>
          u.name.toLowerCase().includes(term) ||
          u.email.toLowerCase().includes(term)
      )
    : users;

  const items = filtered.map((u) => {
    const { disabled, tooltip } = computePropertyAccessState({
      user: u,
      propertyId,
      propertyName,
      requirePropertyAccess,
      hasRelatedObject
    });
    return {
      disabled,
      tooltip,
      entity_identifier: String(u.id),
      entity_name: "partner_user",
      title: u.name,
      id: String(u.id)
    };
  });

  return [{ key: "user", title: "users", items }];
};

export const fetchMentions = async ({
  query,
  applicationId,
  leadId,
  propertyName
}: {
  query: string;
  applicationId?: string;
  leadId?: string;
  propertyName?: string;
}) => {
  const user = await instance
    .get("portal/v1/users", {
      params: {
        search: query,
        application_id: applicationId,
        lead_id: leadId
      }
    })
    .then((res) => res.data);

  const mentionItems = user.items.map(
    (user: { id: number; name: string; can_view_application?: boolean }) => {
      const disabled =
        typeof user?.can_view_application === "boolean" &&
        !user?.can_view_application;
      const tooltip = getMentionTooltip({ propertyName, disabled });

      return {
        disabled,
        tooltip,
        entity_identifier: String(user.id),
        entity_name: "partner_user",
        title: user.name,
        id: String(user.id)
      };
    }
  );

  const suggestionSections = [
    {
      key: "user",
      title: "users",
      items: mentionItems
    }
  ];

  return suggestionSections;
};
